/**
 * Whether the app is working online, and whether what is on the screen came from the device.
 * Having a network connection is not proof that the server answers, so this follows what actually
 * happened to the last requests, as well as the browser's online and offline events.
 */

import { useSyncExternalStore } from "react";

export interface OfflineStatus {
  /** the browser says it has no connection, or the last request could not reach the server */
  offline: boolean;
  /** an answer was shown from the copy on the device: when that copy was fetched, and whether it is too old */
  servedFrom?: { at: string; expired: boolean };
}

let state: OfflineStatus = { offline: typeof navigator !== "undefined" && navigator.onLine === false };
const listeners = new Set<() => void>();

function set(next: OfflineStatus) {
  if (next.offline === state.offline && next.servedFrom?.at === state.servedFrom?.at && next.servedFrom?.expired === state.servedFrom?.expired) return;
  state = next;
  listeners.forEach((l) => l());
}

/** The server could not be reached; an answer may have been taken from the device. */
export function markUnreachable(servedFrom?: { at: string; expired: boolean }): void {
  // keep the oldest copy that has been shown, so the bar tells the truth about how old the screen is
  const keep = state.servedFrom && servedFrom ? (state.servedFrom.at < servedFrom.at ? state.servedFrom : servedFrom) : (servedFrom ?? state.servedFrom);
  set({ offline: true, servedFrom: keep });
}

/** The server answered. */
export function markReachable(): void {
  set({ offline: false });
}

export const getStatus = (): OfflineStatus => state;

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

if (typeof window !== "undefined") {
  window.addEventListener("offline", () => set({ ...state, offline: true }));
  window.addEventListener("online", () => set({ offline: false }));
}

export function useOfflineStatus(): OfflineStatus {
  return useSyncExternalStore(subscribe, getStatus, getStatus);
}

/** For tests. */
export function resetStatusForTests(offline = false): void {
  state = { offline };
  listeners.forEach((l) => l());
}

/** Is this the kind of failure that means the server could not be reached (as opposed to an answer with an error)? */
export const isNetworkError = (e: unknown): boolean => e instanceof TypeError || (e instanceof Error && /failed to fetch|networkerror|load failed|network request failed/i.test(e.message));
